import React, { useEffect, useState } from 'react';
import ReviewTag from '../../../types/ReviewTag';
import AnalyticsService from '../../../services/AnalyticsService/AnalyticsService';

interface TagManagerProps {
  chatbotId: number;
  messageId: number;
  initialTag?: ReviewTag | null;
  onTagChange?: (tag: ReviewTag | null) => void;
  setError: React.Dispatch<React.SetStateAction<string>>;
}

function TagManager({
  chatbotId,
  messageId,
  initialTag = null,
  onTagChange,
  setError
}: Readonly<TagManagerProps>): React.ReactElement | null {
  const [availableTags, setAvailableTags] = useState<ReviewTag[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<number | null>(
    initialTag?.id ?? null
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const fetchTags = async (): Promise<void> => {
      try {
        const tags = await AnalyticsService.getReviewTags();
        setAvailableTags(tags);
      } catch (err: any) {
        if (err.name !== 'CanceledError') {
          const error =
            err.apiError?.message ||
            'Failed to load review tags. Please try again.';
          setError(error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchTags();
  }, [setError, messageId]);

  useEffect(() => {
    setSelectedTagId(initialTag?.id ?? null);
  }, [initialTag]);

  const handleTagClick = async (tag: ReviewTag): Promise<void> => {
    const previousTagId = selectedTagId;
    const nextTagId = selectedTagId === tag.id ? null : tag.id;
    setSelectedTagId(nextTagId);

    try {
      setError('');
      const updatedTag = await AnalyticsService.updateMessageReviewTag(
        chatbotId,
        messageId,
        nextTagId
      );
      onTagChange?.(updatedTag);
    } catch (err: any) {
      setSelectedTagId(previousTagId);
      if (err.name !== 'CanceledError') {
        const error =
          err.apiError?.message || 'Failed to update tag. Please try again.';
        setError(error);
      }
    }
  };

  if (isLoading || availableTags.length === 0) {
    return null;
  }

  const toSentenceCase = (str: string): string =>
    str
      .toLowerCase()
      .replaceAll('_', ' ')
      .replace(/^\w/, (c) => c.toUpperCase());

  return (
    <div className='flex flex-wrap gap-1.5'>
      {availableTags.map((tag) => {
        const isSelected = selectedTagId === tag.id;
        return (
          <button
            key={tag.id}
            type='button'
            aria-pressed={isSelected}
            title={tag.description || undefined}
            onClick={() => handleTagClick(tag)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              isSelected
                ? 'bg-brand-light border-brand-light text-white'
                : 'border-input text-text-secondary hover:bg-muted'
            }`}
          >
            {toSentenceCase(tag.name)}
          </button>
        );
      })}
    </div>
  );
}

export default TagManager;
